import { ArrowLeft, Calendar, FileText, History, Merge, Pencil, Trash2 } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { CitationFocus } from '@/components/case-partner/DocumentViewerContent';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { AuditPanel } from '@/components/knowledge-graph/AuditPanel';
import { ChronologyPanel } from '@/components/knowledge-graph/ChronologyPanel';
import { getTagStyle } from '@/components/knowledge-graph/chronology-panel/styles';
import { DocumentListPanel } from '@/components/knowledge-graph/DocumentListPanel';
import { EntityIcon } from '@/components/knowledge-graph/EntityIcon';
import { EntityPicker } from '@/components/knowledge-graph/EntityPicker';
import { Spinner } from '@/components/shared';
import { TimelineIcon } from '@/components/ui/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  type ChronologyClaim,
  type ChronologyEvent,
  type ChronologyEventLight,
  useEventDetail,
} from '@/hooks/useChronology';
import type { CanonicalEntity } from '@/hooks/useEntities';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { displayRoles, roleWithSide } from '@/lib/role-labels';
import { formatRegionalDate } from '@/utils/date-utils';
import { removeTimestampPrefix } from '@/utils/file-utils';

// ── Types ──────────────────────────────────────────────────────────

type DetailItem =
  | { kind: 'event'; event: ChronologyEventLight }
  | { kind: 'event-history'; event: ChronologyEventLight }
  | { kind: 'entity'; entity: CanonicalEntity }
  | { kind: 'chronology'; entityName: string }
  | { kind: 'documents'; entity: CanonicalEntity };

function detailItemKey(item: DetailItem): string {
  switch (item.kind) {
    case 'event':
      return `event:${item.event.id}`;
    case 'event-history':
      return `event-history:${item.event.id}`;
    case 'entity':
      return `entity:${item.entity.id}`;
    case 'chronology':
      return `chronology:${item.entityName.toLowerCase()}`;
    case 'documents':
      return `documents:${item.entity.id}`;
  }
}

// ── Navigation context ─────────────────────────────────────────────

interface NavStackEntry {
  label: string;
  item: DetailItem;
}

interface DetailNavContextType {
  navigateToEntity: (entityName: string) => void;
  navigateToChronology: (entityName: string) => void;
  navigateToDocuments: (entity: CanonicalEntity) => void;
  navigateToEventHistory: (event: ChronologyEventLight) => void;
  onDocumentClick?: (documentGuid: string, focus?: CitationFocus) => void;
  entities: CanonicalEntity[];
  matterGuid?: string;
  onEntityChanged?: () => void;
  /** Resolves the chronology id for an event so the edit panel can fetch
   *  matter-scoped tags + config. Returns null if the event isn't
   *  linked to a known chronology yet. */
  resolveChronologyId?: (event: ChronologyEventLight) => string | null;
  /** Called after a successful edit / delete / revert so the page can
   *  re-fetch chronology events. May be async — callers can await it to
   *  hold the form open until the timeline has fresh data. */
  onEventChanged?: () => void | Promise<void>;
  /** Opens the page-level event edit sheet for the given event. */
  onEditEvent?: (event: ChronologyEvent) => void;
  /** Deletes an event and lets the page decide what panel state to show next. */
  onDeleteEvent?: (event: ChronologyEvent) => void | Promise<void>;
  /** Tag-category name → colour identifier for styling event rows. */
  colorByCategory?: ReadonlyMap<string, string | null>;
}

const DetailNavContext = createContext<DetailNavContextType>({
  navigateToEntity: () => {},
  navigateToChronology: () => {},
  navigateToDocuments: () => {},
  navigateToEventHistory: () => {},
  entities: [],
});
const useDetailNav = () => useContext(DetailNavContext);

// ── Event detail view ──────────────────────────────────────────────

const SOURCE_DATE_STYLE = {
  dot: 'border-[#C7D4E1]',
  badge: 'text-[#627383] bg-[#F3F7FA]',
};
const SOURCE_FILENAME_TRUNCATION_LENGTH = 44;

type SourceProducer = ChronologyClaim['asserting_entities'][0];
type SourceEntry = {
  doc: ChronologyClaim['source_documents'][0];
  kind: string;
  producers: SourceProducer[];
};

function uniqueProducers(producers: SourceProducer[]): SourceProducer[] {
  const byName = new Map<string, SourceProducer>();
  for (const producer of producers) {
    const key = producer.name.trim().toLowerCase();
    if (key && !byName.has(key)) byName.set(key, producer);
  }
  return Array.from(byName.values());
}

function formatProducerList(producers: SourceProducer[]): string {
  return producers.map(producer => producer.name).join(', ');
}

function truncateSourceFilename(filename: string, maxLength: number = SOURCE_FILENAME_TRUNCATION_LENGTH): string {
  if (filename.length <= maxLength) return filename;

  const lastDot = filename.lastIndexOf('.');
  const hasExtension = lastDot > 0 && lastDot < filename.length - 1;
  if (!hasExtension) return `${filename.slice(0, maxLength - 3)}...`;

  const extension = filename.slice(lastDot);
  const nameWithoutExt = filename.slice(0, lastDot);
  const truncatedBaseLength = Math.max(8, maxLength - extension.length - 3);
  return `${nameWithoutExt.slice(0, truncatedBaseLength)}...${extension}`;
}

function SourceTimeline({ sources, onDocumentClick, eventId }: {
  sources: SourceEntry[];
  onDocumentClick?: (documentGuid: string, focus?: CitationFocus) => void;
  eventId?: string;
}) {
  const { navigateToEntity } = useDetailNav();
  const { locale } = useMatterJurisdiction();

  return (
    <div className="relative">
      <div className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-gray-200" />
      <div className="space-y-0">
        {sources.map(({ doc, producers }) => {
          const dateStr = doc.doc_date ? formatRegionalDate(doc.doc_date, locale) : null;
          const [primaryProducer, ...secondaryProducers] = producers;
          const secondarySummary = secondaryProducers.length > 0
            ? formatProducerList(secondaryProducers)
            : '';
          const fullFilename = doc.filename ? removeTimestampPrefix(doc.filename) : `Doc ${doc.document_id}`;
          const truncatedFilename = truncateSourceFilename(fullFilename);
          const isFilenameTruncated = truncatedFilename !== fullFilename;
          const fileButton = (
            <button
              type="button"
              onClick={() => doc.guid && onDocumentClick?.(doc.guid, eventId ? { type: 'events', canonicalId: eventId } : undefined)}
              className={`flex min-w-0 items-center gap-1 text-[11px] text-gray-600 hover:text-gray-800 transition-colors ${dateStr ? 'mt-1' : ''}`}
            >
              <FileText className="h-2.5 w-2.5 shrink-0" />
              <span className="min-w-0 truncate">{truncatedFilename}</span>
              {(doc.doc_type_label || doc.doc_type) && (
                <span className="shrink-0 text-gray-400">· {doc.doc_type_label || doc.doc_type?.replace(/_/g, ' ')}</span>
              )}
            </button>
          );
          return (
            <div key={doc.document_id} className="relative flex gap-2.5 pb-2.5">
              <div className="relative z-10 mt-1.5 shrink-0">
                <div className={`w-[11px] h-[11px] rounded-full border-2 bg-white ${SOURCE_DATE_STYLE.dot}`} />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                {dateStr && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${SOURCE_DATE_STYLE.badge}`}>
                    {dateStr}
                  </span>
                )}
                {isFilenameTruncated ? (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {fileButton}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs px-2.5 py-2">
                        <p className="text-[11px] leading-relaxed text-gray-700 break-all">{fullFilename}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : fileButton}
                {primaryProducer && (
                  <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[10px] text-gray-400">
                    <span className="shrink-0">from</span>
                    <button
                      type="button"
                      onClick={() => navigateToEntity(primaryProducer.name)}
                      className="min-w-0 truncate text-[10px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      {primaryProducer.name}
                    </button>
                    {secondaryProducers.length > 0 && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="shrink-0 cursor-default text-[10px] font-medium text-gray-400"
                              title={`${secondaryProducers.length} more producers`}
                            >
                              +{secondaryProducers.length}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs px-2.5 py-2">
                            <p className="text-[11px] leading-relaxed text-gray-700">{secondarySummary}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventSources({ claims, onDocumentClick, eventId }: {
  claims: ChronologyClaim[];
  onDocumentClick?: (documentGuid: string, focus?: CitationFocus) => void;
  eventId?: string;
}) {
  const sources = useMemo(() => {
    const seen = new Map<number, SourceEntry>();
    for (const claim of claims) {
      const kind = claim.assertion_kind;
      const producers = claim.asserting_entities || [];
      for (const doc of claim.source_documents || []) {
        const existing = seen.get(doc.document_id);
        if (!existing) {
          seen.set(doc.document_id, { doc, kind, producers: uniqueProducers(producers) });
        } else {
          if (kind === 'observation' && existing.kind !== 'observation') existing.kind = kind;
          existing.producers = uniqueProducers([...existing.producers, ...producers]);
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) => {
      const da = a.doc.doc_date ?? '';
      const db = b.doc.doc_date ?? '';
      return da.localeCompare(db);
    });
  }, [claims]);

  if (sources.length === 0) return null;

  return (
    <div>
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Sources</p>
      <SourceTimeline sources={sources} onDocumentClick={onDocumentClick} eventId={eventId} />
    </div>
  );
}

function EventDetail({ event }: { event: ChronologyEvent }) {
  const {
    navigateToEntity, onDocumentClick,
    matterGuid, resolveChronologyId, onDeleteEvent, onEditEvent,
    navigateToEventHistory, colorByCategory,
  } = useDetailNav();
  const [deleting, setDeleting] = useState(false);

  const chronologyId = resolveChronologyId?.(event) ?? null;
  const canEdit = !!matterGuid && !!chronologyId && !!onEditEvent;
  const canDelete = !!matterGuid && !!onDeleteEvent;
  const canViewHistory = !!matterGuid;

  const handleDelete = useCallback(async () => {
    if (!onDeleteEvent) return;
    if (!confirm('Delete this event? It will be hidden but can be undeleted from the timeline.')) return;
    setDeleting(true);
    try {
      await onDeleteEvent(event);
    } finally {
      setDeleting(false);
    }
  }, [event, onDeleteEvent]);

  const visibleEntities = (event.entities ?? event.direct_entities ?? [])
    .filter(e => e.role !== 'asserting');

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <EventTagPills tags={event.tags} colorByCategory={colorByCategory} />
          </div>
          <div className="flex items-center gap-3">
            {canViewHistory && (
              <button
                type="button"
                onClick={() => navigateToEventHistory(event)}
                className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
              >
                <History className="h-3 w-3" />
                History
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => onEditEvent?.(event)}
                className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-700 transition-colors"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => { void handleDelete(); }}
                disabled={deleting}
                aria-label={deleting ? 'Deleting event' : 'Delete event'}
                title={deleting ? 'Deleting event' : 'Delete event'}
                className="flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
              >
                {deleting ? <Spinner /> : <Trash2 className="h-3 w-3" />}
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-800 leading-relaxed">{event.description}</p>
        {event.detail && (
          <div className="mt-2 text-xs text-gray-600 leading-relaxed whitespace-pre-line border-l-2 border-gray-200 pl-3">
            {event.detail}
          </div>
        )}
      </div>

      {event.temporal_display && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Calendar className="h-3.5 w-3.5 text-gray-400" />
          {event.temporal_display}
        </div>
      )}

      {visibleEntities.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Entities Involved</p>
          <div className="space-y-1">
            {visibleEntities
              .slice()
              .sort((a, b) => {
                const aIsOrg = a.entity_type === 'organization' ? 1 : 0;
                const bIsOrg = b.entity_type === 'organization' ? 1 : 0;
                return aIsOrg - bIsOrg;
              })
              .map((ent) => (
              <button
                type="button"
                key={`${ent.name}-${ent.role ?? 'entity'}-${ent.entity_type ?? 'unknown'}`}
                onClick={() => navigateToEntity(ent.name)}
                className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors group"
              >
                <EntityIcon type={ent.entity_type as 'person' | 'organization' | 'other' | undefined} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-700">{ent.name}</p>
                </div>
                <ArrowLeft className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100 rotate-180 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      )}

      <EventSources claims={event.claims} onDocumentClick={onDocumentClick} eventId={event.id} />

    </div>
  );
}

function EventDetailLoading({ event }: { event: ChronologyEventLight }) {
  const { colorByCategory } = useDetailNav();

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <EventTagPills tags={event.tags} colorByCategory={colorByCategory} />
          </div>
        </div>
        <p className="text-sm text-gray-800 leading-relaxed">{event.description}</p>
        {event.detail && (
          <div className="mt-2 text-xs text-gray-600 leading-relaxed whitespace-pre-line border-l-2 border-gray-200 pl-3">
            {event.detail}
          </div>
        )}
      </div>
      {event.temporal_display && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Calendar className="h-3.5 w-3.5 text-gray-400" />
          {event.temporal_display}
        </div>
      )}
      <div className="space-y-3 pt-1">
        <div className="h-3 w-28 rounded-sm bg-gray-100 animate-pulse" />
        <div className="space-y-2">
          <div className="h-8 rounded-sm bg-gray-50 animate-pulse" />
          <div className="h-8 rounded-sm bg-gray-50 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function EventTagPills({
  tags,
  colorByCategory,
}: {
  tags: ChronologyEventLight['tags'];
  colorByCategory?: ReadonlyMap<string, string | null>;
}) {
  if (!tags?.length) {
    return (
      <span className="text-xs font-medium leading-5 text-gray-400">
        Untagged
      </span>
    );
  }

  return (
    <p
      title={tags.map(tag => tag.label).join(' · ')}
      className="flex min-w-0 flex-wrap items-center gap-y-0.5 overflow-hidden text-xs font-medium leading-5"
    >
      {tags.map((tag, index) => {
        const category = tag.group_label || tag.group_key;
        const style = getTagStyle(tag.group_color, category, colorByCategory);
        const textClass = index === 0 ? style.pill.split(' ').find(part => part.startsWith('text-')) ?? style.text : 'text-gray-400';
        return (
          <span
            key={tag.id}
            className="inline-flex max-w-full items-center"
            title={category ? `${category}: ${tag.label}` : tag.label}
          >
            {index > 0 && <span className="mx-1.5 shrink-0 text-[13px] font-semibold leading-5 text-gray-400">·</span>}
            <span className={`min-w-0 truncate ${textClass}`}>{tag.label}</span>
          </span>
        );
      })}
    </p>
  );
}

function EventDetailPanel({ event }: { event: ChronologyEventLight }) {
  const { matterGuid } = useDetailNav();
  const { event: detail } = useEventDetail(matterGuid, event.id, event.chronology_id);
  return detail ? <EventDetail event={detail} /> : <EventDetailLoading event={event} />;
}

function EventHistoryView({ event }: { event: ChronologyEventLight }) {
  const { matterGuid, onEventChanged } = useDetailNav();
  if (!matterGuid) return null;

  return (
    <div className="h-full -mx-4 -my-3">
      <AuditPanel
        key={event.id}
        kind="event"
        matterGuid={matterGuid}
        eventGuid={event.id}
        onChanged={() => { void onEventChanged?.(); }}
      />
    </div>
  );
}

// ── Entity detail view ─────────────────────────────────────────────

function RoleBadge({ role }: { role: import('@/hooks/useEntities').EntityRole }) {
  const { jurisdiction } = useMatterJurisdiction();
  return (
    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
      {roleWithSide(role.role_type, role.side_alignment, jurisdiction.ui_labels)}
    </span>
  );
}

function MergeEntityPicker({ entity }: { entity: CanonicalEntity }) {
  const { entities, matterGuid, onEntityChanged } = useDetailNav();
  const [loading, setLoading] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CanonicalEntity | null>(null);

  const candidates = useMemo(
    () => entities.filter(e => e.id !== entity.id),
    [entities, entity.id],
  );

  const handleMerge = async (target: CanonicalEntity) => {
    setLoading(true);
    try {
      await backendClient.post(`/entities/${matterGuid}/${target.id}/merge`, {
        source_entity_guid: entity.id,
      });
      toast.success(`Merged ${entity.name} into ${target.name}`);
      onEntityChanged?.();
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Error merging entities');
    } finally {
      setLoading(false);
      setConfirmTarget(null);
    }
  };

  if (!matterGuid) return null;

  if (confirmTarget) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 space-y-2.5">
        <p className="text-[11px] text-gray-600 leading-relaxed">
          Merge <span className="font-medium text-gray-800">{entity.name}</span> into <span className="font-medium text-gray-800">{confirmTarget.name}</span>?
          <br />
          <span className="text-gray-400">This entity will be removed and its observations transferred.</span>
        </p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleMerge(confirmTarget)}
            disabled={loading}
            className="flex items-center gap-1.5 text-[11px] font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 px-2.5 py-1 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? <Spinner /> : <Merge className="h-3 w-3" />}
            Merge
          </button>
          <button
            type="button"
            onClick={() => setConfirmTarget(null)}
            disabled={loading}
            className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <EntityPicker
      candidates={candidates}
      onPick={setConfirmTarget}
      triggerLabel="Merge into..."
      triggerIcon={Merge}
      searchPlaceholder="Search entity to merge into..."
      showEntityIcon
      maxListHeight="md"
      emptyMessage="No matching entities"
    />
  );
}

function DeleteEntityButton({ entity }: { entity: CanonicalEntity }) {
  const { matterGuid, onEntityChanged } = useDetailNav();
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!matterGuid) return null;

  if (confirm) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={async () => {
            setLoading(true);
            try {
              await backendClient.delete(`/entities/${matterGuid}/${entity.id}`);
              toast.success(`Deleted ${entity.name}`);
              onEntityChanged?.();
            } catch (err: unknown) {
              toast.error(apiErrorMessage(err) || 'Error deleting entity');
            } finally {
              setLoading(false);
              setConfirm(false);
            }
          }}
          disabled={loading}
          className="flex items-center gap-1.5 text-[11px] text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
        >
          {loading ? <Spinner /> : <Trash2 className="h-3 w-3" />}
          Confirm delete
        </button>
        <button
          type="button"
          onClick={() => setConfirm(false)}
          disabled={loading}
          className="text-[11px] text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-red-500 transition-colors"
    >
      <Trash2 className="h-3 w-3" />
      Delete
    </button>
  );
}

function EntityDetail({ entity, eventCount }: { entity: CanonicalEntity; eventCount: number }) {
  const { navigateToChronology, navigateToDocuments } = useDetailNav();
  const roles = useMemo(() => displayRoles(entity.roles), [entity.roles]);
  const primary = roles[0];
  const otherRoles = roles.slice(1);

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2.5 mb-1">
          <EntityIcon type={entity.entity_type} size="md" />
          <p className="text-sm font-medium text-gray-800">{entity.name}</p>
        </div>
        {primary && <RoleBadge role={primary} />}
      </div>

      {entity.description && (
        <p className="text-xs text-gray-600 leading-relaxed">{entity.description}</p>
      )}

      {entity.aliases.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Also known as</p>
          <div className="flex flex-wrap gap-1">
            {entity.aliases.map(alias => (
              <span key={alias} className="text-[10px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">{alias}</span>
            ))}
          </div>
        </div>
      )}

      {otherRoles.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Other roles</p>
          <div className="space-y-1">
            {otherRoles.map((r) => (
              <RoleBadge key={`${r.role_type}-${r.side_alignment}`} role={r} />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {eventCount > 0 && (
          <button
            type="button"
            onClick={() => navigateToChronology(entity.name)}
            className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors group"
          >
            <TimelineIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="text-xs text-gray-600">
              View {entity.name}&apos;s chronology ({eventCount} event{eventCount !== 1 ? 's' : ''})
            </span>
            <ArrowLeft className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100 rotate-180 transition-opacity ml-auto shrink-0" />
          </button>
        )}

        {entity.document_guids.length > 0 && (
          <button
            type="button"
            onClick={() => navigateToDocuments(entity)}
            className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors group"
          >
            <FileText className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="text-xs text-gray-600">
              View {entity.document_guids.length} document{entity.document_guids.length !== 1 ? 's' : ''} mentioning {entity.name}
            </span>
            <ArrowLeft className="h-3 w-3 text-gray-300 opacity-0 group-hover:opacity-100 rotate-180 transition-opacity ml-auto shrink-0" />
          </button>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 space-y-2">
        <MergeEntityPicker entity={entity} />
        <DeleteEntityButton entity={entity} />
      </div>
    </div>
  );
}

// ── Filtered chronology view ───────────────────────────────────────

function FilteredChronologyView({ entityName, events, onEventSelect, matterGuid, colorByCategory }: {
  entityName: string;
  events: ChronologyEventLight[];
  onEventSelect: (event: ChronologyEventLight) => void;
  matterGuid?: string;
  colorByCategory?: ReadonlyMap<string, string | null>;
}) {
  const filtered = useMemo(() => {
    const lower = entityName.toLowerCase();
    return events.filter(ev =>
      ev.entity_names.some(name => name.toLowerCase() === lower)
    );
  }, [events, entityName]);

  return (
    <div className="h-full -mx-4 -my-3">
      <ChronologyPanel
        events={filtered}
        matterGuid={matterGuid}
        colorByCategory={colorByCategory}
        onEventSelect={(ev) => { if (ev) onEventSelect(ev); }}
      />
    </div>
  );
}

// ── Document list view (inside stack) ──────────────────────────────

function DocumentsView({ entity, onDocumentClick }: {
  entity: CanonicalEntity;
  onDocumentClick?: (documentGuid: string, focus?: CitationFocus) => void;
}) {
  const handleClick = useCallback((docGuid: string) => {
    onDocumentClick?.(docGuid, { type: 'entities', canonicalId: entity.id });
  }, [entity.id, onDocumentClick]);

  return (
    <div className="h-full -mx-4 -my-3">
      <DocumentListPanel
        title={`Documents mentioning ${entity.name}`}
        documentGuids={entity.document_guids}
        onDocumentClick={handleClick}
      />
    </div>
  );
}

// ── Navigator (case theory side panel) ─────────────────────────────

interface DetailNavigatorProps {
  item: DetailItem;
  events: ChronologyEventLight[];
  entities: CanonicalEntity[];
  /** Opens the document viewer tab alongside case theory (not replacing it) */
  onDocumentClick?: (documentGuid: string, focus?: CitationFocus) => void;
  matterGuid?: string;
  onEntityChanged?: () => void;
  /** Resolves the chronology id for an event so EventDetail can surface an
   *  Edit button that opens the page-level edit sheet. */
  resolveChronologyId?: (event: ChronologyEventLight) => string | null;
  onEventChanged?: () => void | Promise<void>;
  /** Opens the page-level event edit sheet for the given event. */
  onEditEvent?: (event: ChronologyEvent) => void;
  /** Deletes an event and lets the page decide what panel state to show next. */
  onDeleteEvent?: (event: ChronologyEvent) => void | Promise<void>;
  /** Category-name → colour identifier for styling event rows. */
  colorByCategory?: ReadonlyMap<string, string | null>;
}

export function DetailNavigator({
  item, events, entities, onDocumentClick, matterGuid, onEntityChanged,
  resolveChronologyId, onEventChanged, onDeleteEvent, onEditEvent, colorByCategory,
}: DetailNavigatorProps) {
  const [stack, setStack] = useState<NavStackEntry[]>([]);
  const rootItemKeyRef = useRef(detailItemKey(item));

  // Reset stack only when the logical root changes, not when the parent
  // rebuilds the same root item with a fresh object reference.
  useEffect(() => {
    const nextRootItemKey = detailItemKey(item);
    if (rootItemKeyRef.current === nextRootItemKey) return;
    rootItemKeyRef.current = nextRootItemKey;
    setStack([]);
  }, [item]);

  const currentItem = stack.length > 0 ? stack[stack.length - 1].item : item;

  const navigateToEntity = useCallback((entityName: string) => {
    const canonical = entities.find(e =>
      e.name.toLowerCase() === entityName.toLowerCase() ||
      e.aliases.some(a => a.toLowerCase() === entityName.toLowerCase())
    );
    if (canonical) {
      setStack(prev => [...prev, { label: canonical.name, item: { kind: 'entity', entity: canonical } }]);
    }
  }, [entities]);

  const navigateToChronology = useCallback((entityName: string) => {
    setStack(prev => [...prev, { label: `${entityName}'s chronology`, item: { kind: 'chronology', entityName } }]);
  }, []);

  const navigateToDocuments = useCallback((entity: CanonicalEntity) => {
    setStack(prev => [...prev, { label: `${entity.name}'s documents`, item: { kind: 'documents', entity } }]);
  }, []);

  const navigateToEventHistory = useCallback((event: ChronologyEventLight) => {
    setStack(prev => [...prev, { label: 'History', item: { kind: 'event-history', event } }]);
  }, []);

  const handleChronologyEventSelect = useCallback((event: ChronologyEventLight | null) => {
    if (event) {
      setStack(prev => [...prev, { label: event.description.slice(0, 40), item: { kind: 'event', event } }]);
    }
  }, []);

  const goBack = useCallback(() => {
    setStack(prev => prev.slice(0, -1));
  }, []);

  const backLabel = useMemo(() => {
    if (stack.length === 0) return null;
    if (stack.length === 1) {
      if (item.kind === 'event') return item.event.description.slice(0, 40) + (item.event.description.length > 40 ? '...' : '');
      if (item.kind === 'entity') return item.entity.name;
      return 'previous';
    }
    return stack[stack.length - 2].label;
  }, [stack, item]);

  const eventCount = useMemo(() => {
    if (currentItem.kind !== 'entity') return 0;
    const name = currentItem.entity.name.toLowerCase();
    const aliases = new Set(currentItem.entity.aliases.map(a => a.toLowerCase()));
    return events.filter(ev =>
      ev.entity_names.some(entityName => {
        const lower = entityName.toLowerCase();
        return lower === name || aliases.has(lower);
      })
    ).length;
  }, [currentItem, events]);

  return (
    <DetailNavContext.Provider value={{
      navigateToEntity, navigateToChronology, navigateToDocuments, navigateToEventHistory,
      onDocumentClick, entities, matterGuid, onEntityChanged,
      resolveChronologyId, onEventChanged, onDeleteEvent, onEditEvent, colorByCategory,
    }}>
      <div className="h-full overflow-y-auto px-4 py-3">
        {stack.length > 0 && (
          <button type="button" onClick={goBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3 transition-colors">
            <ArrowLeft className="h-3 w-3" />
            <span className="truncate">Back to {backLabel}</span>
          </button>
        )}

        {currentItem.kind === 'event' ? (
          <EventDetailPanel event={currentItem.event} />
        ) : currentItem.kind === 'event-history' ? (
          <EventHistoryView event={currentItem.event} />
        ) : currentItem.kind === 'entity' ? (
          <EntityDetail entity={currentItem.entity} eventCount={eventCount} />
        ) : currentItem.kind === 'chronology' ? (
          <FilteredChronologyView
            entityName={currentItem.entityName}
            events={events}
            matterGuid={matterGuid}
            colorByCategory={colorByCategory}
            onEventSelect={handleChronologyEventSelect}
          />
        ) : (
          <DocumentsView
            entity={currentItem.entity}
            onDocumentClick={onDocumentClick}
          />
        )}
      </div>
    </DetailNavContext.Provider>
  );
}
